import React from 'react';
import { Modal, Button } from '@shared/components';
import type { DiffEntry } from './computeCustomerDiff';

interface EditCustomerConfirmModalProps {
    isOpen: boolean;
    diff: DiffEntry[];
    isSubmitting: boolean;
    onCancel: () => void;
    onConfirm: () => void;
    /** Overrides the default dialog heading. */
    heading?: string;
    /** Rendered above the diff, for a change that needs a warning with it. */
    notice?: React.ReactNode;
}

export const EditCustomerConfirmModal: React.FC<EditCustomerConfirmModalProps> = ({
    isOpen,
    diff,
    isSubmitting,
    onCancel,
    onConfirm,
    heading = 'Confirm changes',
    notice,
}) => (
    <Modal
        isOpen={isOpen}
        onClose={onCancel}
        ariaLabelledBy="edit-customer-confirm-heading"
        className="max-w-2xl p-6"
    >
        <h2 id="edit-customer-confirm-heading" className="text-lg font-semibold mb-4">
            {heading}
        </h2>
        {notice}
        <p className="text-sm text-mp-muted mb-4">
            The following fields will be updated. Review carefully before confirming.
        </p>
        <table className="w-full text-sm">
            <thead>
                <tr className="border-b border-mp-border">
                    <th className="text-left py-2">Field</th>
                    <th className="text-left py-2">Original</th>
                    <th className="text-left py-2">New</th>
                </tr>
            </thead>
            <tbody>
                {diff.map((entry) => (
                    <tr key={entry.field} className="border-b border-mp-border">
                        <td className="py-2 font-medium">{entry.label}</td>
                        <td className="py-2 text-mp-muted">
                            {entry.originalValue || <em className="text-mp-muted">(empty)</em>}
                        </td>
                        <td className="py-2 font-semibold">
                            {entry.newValue || <em className="text-mp-muted">(empty)</em>}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
        <div className="flex justify-end gap-2 mt-6">
            <Button onClick={onCancel} variant="clear" disabled={isSubmitting}>
                Cancel
            </Button>
            <Button onClick={onConfirm} disabled={isSubmitting}>
                {isSubmitting ? 'Saving…' : 'Confirm'}
            </Button>
        </div>
    </Modal>
);
